import { create } from "zustand";
import type {
  PayoutMethodType,
  PayoutInput,
  RecipientInput,
} from "../interfaces/payout";
import type { RecipientDestination } from "../api/recipients";
import type { FundingInstrument } from "../interfaces/funding";
import type { FundingMethod } from "../utils/catalog";
import type { EstimateOption } from "../api/pricing";

/**
 * The armed (pre-confirm) payout selection: which payout method, the concrete
 * pre-quote option matched, and the collected payout input.
 *
 * `origin` separates the requester's snapshot (seeded from the claim) from a
 * destination the payer actually chose. The details form only pre-fills a
 * `user` selection, and an unquotable `user` selection is an inline error
 * rather than the claim-level "route unavailable" screen.
 */
export interface ComposeSelection {
  method: PayoutMethodType;
  option: EstimateOption | null;
  payout: PayoutInput;
  origin: "claim" | "user";
}

/**
 * In-memory compose state for the transfer screen. Lives in a module-level
 * zustand store (NOT persisted) so it survives client-side navigation — most
 * importantly the add-card round trip that unmounts TransferPage. It is reset
 * explicitly when a fresh send starts (HomePage "new send") and after a send
 * completes; the add-card round trip deliberately does NOT reset it.
 */
interface ComposeState {
  /** Editable origin country (Patrón B). Null until seeded from config. */
  originCountry: string | null;
  setOriginCountry: (country: string) => void;

  /** Editable destination country (Patrón B). Null until seeded from config. */
  destinationCountry: string | null;
  setDestinationCountry: (country: string) => void;

  /** Editable destination currency (receive leg). Null until seeded from catalog. */
  destinationCurrency: string | null;
  setDestinationCurrency: (currency: string) => void;

  /** Editable source currency (send leg). Null until seeded from claim. */
  sourceCurrency: string | null;
  setSourceCurrency: (currency: string) => void;

  funding: FundingMethod | null;
  setFunding: (funding: FundingMethod | null) => void;

  /** Registered card backing a CARD funding method (null for cash/other). */
  fundingInstrument: FundingInstrument | null;
  setFundingInstrument: (instrument: FundingInstrument | null) => void;

  selection: ComposeSelection | null;
  setSelection: (selection: ComposeSelection | null) => void;

  recipient: RecipientInput | null;
  setRecipient: (recipient: RecipientInput | null) => void;

  /**
   * Habitual payout destinations from a recipient-book pick. Empty when the
   * user typed a new name. Consumed by PayoutDetailsSlot for one-tap replay.
   */
  bookDestinations: RecipientDestination[];
  setBookDestinations: (destinations: RecipientDestination[]) => void;

  resetCompose: () => void;
}

const initialState = {
  originCountry: null as string | null,
  destinationCountry: null as string | null,
  destinationCurrency: null as string | null,
  sourceCurrency: null as string | null,
  funding: null as FundingMethod | null,
  fundingInstrument: null as FundingInstrument | null,
  selection: null as ComposeSelection | null,
  recipient: null as RecipientInput | null,
  bookDestinations: [] as RecipientDestination[],
};

export const useComposeStore = create<ComposeState>((set) => ({
  ...initialState,
  setOriginCountry: (originCountry) => set(() => ({ originCountry })),
  setDestinationCountry: (destinationCountry) => set(() => ({ destinationCountry })),
  setDestinationCurrency: (destinationCurrency) =>
    set(() => ({ destinationCurrency })),
  setSourceCurrency: (sourceCurrency) => set(() => ({ sourceCurrency })),
  setFunding: (funding) => set(() => ({ funding })),
  setFundingInstrument: (fundingInstrument) => set(() => ({ fundingInstrument })),
  setSelection: (selection) => set(() => ({ selection })),
  setRecipient: (recipient) => set(() => ({ recipient })),
  setBookDestinations: (bookDestinations) => set(() => ({ bookDestinations })),
  resetCompose: () => set(() => ({ ...initialState })),
}));
